import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import { SimpleTrace } from '../../../domain/Trace'
import { TraceSummarySingleView } from '../../../domain/TraceSummary'
import BoxBody from '../../../helper/boxBody'
import Shorten from '../../../helper/shorten'
import { ApplicationState } from '../../../redux/ApplicationState'
import { shareTrace, shareTraceComparison, ShareTraceComparisonParams, ShareTraceParams } from '../../../redux/V2/Trace/actions'
import Router from '../../../router'
import { Box, BoxHeader, BoxTitle } from '../../../ui/box'
import { Col, Row } from '../../../ui/grid'
import ShareTraceFormSettings from './share-trace-form-settings'

interface OwnProps {
    onExit: () => void
    trace: SimpleTrace
    baseTrace?: SimpleTrace
    error?: string | null
    traceSummary: TraceSummarySingleView
    baseTraceSummary?: TraceSummarySingleView
    hasActiveShare: boolean
    allowedToShare: boolean
    defaultRetentionDays: number
}

interface StateProps {
    router: Router
}

interface DispatchProps {
    shareTrace: (params: ShareTraceParams) => void
    shareTraceComparison: (params: ShareTraceComparisonParams) => void
}

type Props = OwnProps & DispatchProps & RouteComponentProps & StateProps

interface State {
    duration: number
    processing: boolean
    error: string | null
    url: string | undefined
    copied: boolean
}

class ShareTraceForm extends React.Component<Props, State> {
    public state: State = {
        duration: 0,
        error: null,
        processing: false,
        url: undefined,
        copied: false,
    }

    public render = (): React.ReactNode => {
        return (
            <Row>
                <Col widthLarge={6} centered>
                    <Box>
                        <BoxHeader>
                            <BoxTitle>Share Trace{this.props.baseTrace ? ' Comparison' : ''}</BoxTitle>
                        </BoxHeader>
                        <hr className="tw-mt-8 tw-mb-8 tw-bg-grey-50 tw-color-grey-50" style={{ height: 2 }} />
                        <BoxBody noPadding>
                            <div className="row">
                                <div className="col-lg-12">
                                    {this.state.error && (
                                        <div className="alert alert-danger">
                                            <h4>
                                                <i className="fa fa-warning" /> Error Processing Form
                                            </h4>
                                            <p>{this.state.error}</p>
                                        </div>
                                    )}
                                    {this.props.allowedToShare ? <div>{this.renderForm()}</div> : null}
                                    {!this.props.allowedToShare ? <div>{this.renderNotAllowed()}</div> : null}
                                </div>
                            </div>
                        </BoxBody>
                    </Box>
                </Col>
            </Row>
        )
    }

    private renderNotAllowed = () => {
        const traceShareUrl = this.getTraceShareUrl()

        return (
            <>
                <div className="alert alert-info">
                    <p>You are not allowed to share traces due to an organization policy setting.</p>

                    <p>
                        Ask an administrator of the organization to change the &quot;Who is allowed to share traces?&quot; setting in the
                        &quot;Users&quot; settings menu of the organization.
                    </p>
                </div>

                {traceShareUrl && this.props.traceSummary.hasActiveShare ? this.renderCopyTraceShareUrlInput(traceShareUrl) : null}

                <div className="centered tw-p-8 tw-bg-grey-50 tw-mt-8">
                    <button className="btn btn-secondary" onClick={this.cancel}>
                        close
                    </button>
                </div>
            </>
        )
    }

    private renderCopyTraceShareUrlInput(traceShareUrl: string) {
        return (
            <Row>
                <Col width={2}>
                    <label className="control-label tw-ml-8">URL</label>
                </Col>
                <Col width={10}>
                    <div className="input-group pr-8">
                        <input readOnly type="text" id="copyURL" className="form-control" value={traceShareUrl} />

                        {this.clipboardApiAvailable() && (
                            <div className="input-group-btn">
                                <a
                                    title="copy url to clipboard"
                                    className="btn btn-default"
                                    onClick={event => this.copyToClipboard(event, traceShareUrl)}
                                >
                                    {this.state.copied ? 'Copied!' : 'Copy'}
                                </a>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        )
    }

    private renderForm = () => {
        const { trace } = this.props
        const traceShareUrl = this.getTraceShareUrl()

        return (
            <form name="share-trace-form" className="share-trace-form" onSubmit={ev => this.submit(ev)}>
                <Row>
                    <Col width={2}>
                        <label className="control-label tw-ml-8">Trace</label>
                    </Col>
                    <Col width={10}>
                        <Shorten string={trace.transaction} />
                    </Col>
                </Row>

                <hr className="tw-mt-8 tw-mb-8 tw-bg-grey-50 tw-color-grey-50" style={{ height: 2 }} />

                <ShareTraceFormSettings
                    traceSummary={this.props.traceSummary}
                    duration={this.state.duration}
                    onChangeDuration={this.changeDuration}
                    defaultRetentionDays={this.props.defaultRetentionDays}
                />

                {traceShareUrl && this.props.traceSummary.hasActiveShare ? (
                    <span>
                        <hr className="tw-mt-8 tw-mb-8 tw-bg-grey-50 tw-color-grey-50" style={{ height: 2 }} />
                        {this.renderCopyTraceShareUrlInput(traceShareUrl)}

                        <div className="centered tw-p-8 tw-bg-grey-50 tw-mt-8">
                            <button className="btn btn-secondary" onClick={this.cancel}>
                                close
                            </button>
                        </div>
                    </span>
                ) : (
                    <div className="centered tw-p-8 tw-bg-grey-50">
                        <button className="btn btn-primary">
                            confirm
                            {this.state.processing && <i className="fa fa-spinner fa-spin" />}
                        </button>
                        <button className="btn btn-secondary tw-ml-4" onClick={this.cancel}>
                            cancel
                        </button>
                    </div>
                )}
            </form>
        )
    }

    private getTraceShareUrl = (): string | null => {
        const { sharedTraceInfo } = this.props.traceSummary
        const { router } = this.props

        if (!sharedTraceInfo?.sharedTraceId) {
            return null
        }

        if (!this.props.baseTraceSummary?.sharedTraceInfo) {
            return router.absoluteUrl('XhprofCoreBundle.Public.sharedTrace', { shareId: sharedTraceInfo.sharedTraceId })
        }

        return router.absoluteUrl('XhprofCoreBundle.Public.sharedTrace.compare', {
            shareId: sharedTraceInfo.sharedTraceId,
            baseShareId: this.props.baseTraceSummary.sharedTraceInfo.sharedTraceId,
        })
    }

    private changeDuration = (ev: React.ChangeEvent<any>) => {
        const { value } = ev.target
        this.setState({ duration: Number(value) })
    }

    private submit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault()
        this.setState({ processing: true })

        try {
            const id = this.props.trace.id
            const baseId = this.props.baseTrace?.id
            const shareDuration = this.state.duration

            if (baseId) {
                this.props.shareTraceComparison({ id, baseId, shareDuration })
            } else {
                this.props.shareTrace({ id, shareDuration })
            }
        } catch (error) {
            this.setState({ error: error.message, processing: false })
        }
    }

    private cancel = ev => {
        ev.preventDefault()
        this.props.onExit()
    }

    private copyToClipboard = (event, code) => {
        event.preventDefault()
        navigator.clipboard.writeText(code).then(() => this.setState({ copied: true }))
    }

    private clipboardApiAvailable = () => {
        return navigator.clipboard && typeof navigator.clipboard.writeText !== 'undefined'
    }
}

export default withRouter(
    connect<StateProps, DispatchProps, OwnProps>(
        (globalState: ApplicationState, props: Props): StateProps => {
            return {
                router: globalState.tidewaysContext.router,
            }
        },
        { shareTrace, shareTraceComparison },
    )(ShareTraceForm),
)
